import RequestService from "./request-service";
import Utils from "./utils";

export default class LinkInterceptor {
  #uid;
  #$links;
  #pageOrigin;

  // onInterceptCallbacks is a map of arrays of callbacks for a particular url
  // The callbacks are called prior to sending the GET request.
  // Callbacks are passed the href of the request object and can edit the href
  // or return a falsey value to cancel the request
  #onInterceptCallbacks;

  // onResponseCallbacks is a map of arrays of callbacks for a particular url
  // The callbacks are called with a promise which resolves to the response of the GET request.
  #onResponseCallbacks;

  constructor() {
    const url = new URL(window.location.href);
    this.#uid = Utils.randomId();
    this.#pageOrigin = url.origin;
  }

  init() {
    this.#bindEvents();
    this.#onInterceptCallbacks = new Map();
    this.#onResponseCallbacks = new Map();
  }

  onIntercept(id, callback, ...hrefs) {
    for (const href of hrefs) {
      const mapKey = this.#getMapKey(href);
      let registered = this.#onInterceptCallbacks.get(mapKey) ?? new Map();
      // Replaces the callback if the id already exists
      registered.set(id, callback);
      this.#onInterceptCallbacks.set(mapKey, registered);
    }
  }

  removeInterceptCallback(id, ...hrefs) {
    for (const href of hrefs) {
      const mapKey = this.#getMapKey(href);
      const registered = this.#onInterceptCallbacks.get(mapKey) ?? new Map();
      registered.delete(id);
      if (registered.size == 0) {
        this.#onInterceptCallbacks.delete(mapKey);
      }
    }
  }

  onResponse(id, callback, ...hrefs) {
    for (const href of hrefs) {
      const mapKey = this.#getMapKey(href);
      const registered = this.#onResponseCallbacks.get(mapKey) ?? new Map();
      // Replaces the callback if the id already exists
      registered.set(id, callback);
      this.#onResponseCallbacks.set(mapKey, registered);
    }
  }

  removeResponseCallback(id, ...hrefs) {
    for (const href of hrefs) {
      const mapKey = this.#getMapKey(href);
      const registered = this.#onResponseCallbacks.get(mapKey) ?? new Map();
      registered.delete(id);
      if (registered.size == 0) {
        this.#onResponseCallbacks.delete(mapKey);
      }
    }
  }

  rebindEvents() {
    this.#bindEvents();
  }

  #bindEvents() {
    this.#$links = $("a");

    // Ensure don't multi-bind
    const clickEv = "click." + this.#uid;
    const keydownEv = "keydown." + this.#uid;

    this.#$links.off(clickEv);
    this.#$links.off(keydownEv);

    this.#$links.on(clickEv, (e) => this.#handleLinkClick(e));

    this.#$links.on(keydownEv, (e) => {
      if (e.keyCode === 13) {
        this.#handleLinkClick(e);
      }
    });
  }

  async #handleLinkClick(e) {
    const href = $(e.currentTarget).attr("href");
    const mapKey = this.#getMapKey(href);

    const icb = this.#onInterceptCallbacks.get(mapKey) ?? new Set();

    // Ignore links which have no intercept handlers
    if (icb.size == 0) {
      return;
    }

    e.preventDefault();

    // Call the registered intercept callbacks
    // and GET the response from the (possibly altered) url.
    // Cache the response promises, so multiple handlers don't cause multiple requests.
    const cachedResponses = new Map();
    for (const kvp of icb) {
      // const id = kvp[0]; // unused
      const cb = kvp[1]; // value
      const newHref = await cb(href);
      if (newHref) {
        const newMapKey = this.#getMapKey(newHref);
        let responseProm = cachedResponses.get(newMapKey);
        if (!responseProm) {
          responseProm = RequestService.navigate(newHref);
          cachedResponses.set(newMapKey, responseProm);
        }
      }
    }

    // For each response to a different href
    // call the registered response callbacks
    for (const [href, response] of cachedResponses) {
      const mapKey = this.#getMapKey(href);
      const rcb = this.#onResponseCallbacks.get(mapKey) ?? new Set();
      for (const kvp of rcb) {
        // const id = kvp[0]; // unused
        const cb = kvp[1]; // value
        await cb({ href, response });
      }
    }

    // Ensure any new page links are intercepted
    Promise.all(cachedResponses.values())
      .then(() => {
        this.#bindEvents();
      })
  }

  #getMapKey(href) {
    // The base param in the URL constructor is used only if the link is relative
    const url = new URL(href, this.#pageOrigin);
    // If the url is internal then normalise to the use the relative path 
    if (url.origin === this.#pageOrigin) {
      href = url.pathname;
    }
    return href;
  }
}
